import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Handshake, Copy, Share2, Loader2, IndianRupee, Users, TrendingUp, Wallet, Building2, ExternalLink, Search, Gift, UserCheck, UserX, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatINR } from "../lib/api";
import Seo from "../components/Seo";

/**
 * GPM Partner Dashboard — role-locked. Shows ONLY four sections:
 *   1. 🤝 GPM Partner Profile
 *   2. 🏠 All GPM Properties (browse + share)
 *   3. 🎁 Refer & Earn
 *   4. 👥 My Team
 * No broker / seller / buyer / service-role widgets — those live on their own dashboards.
 */
export default function PartnerDashboard() {
    const [me, setMe] = useState(null);
    const [stats, setStats] = useState(null);
    const [team, setTeam] = useState(null);
    const [properties, setProperties] = useState([]);
    const [q, setQ] = useState("");
    const nav = useNavigate();

    const load = async () => {
        try {
            const [m, s, t] = await Promise.all([
                api.get("/partner/me"),
                api.get("/partner/stats").catch(() => ({ data: null })),
                api.get("/partner/team").catch(() => ({ data: null })),
            ]);
            setMe(m.data);
            setStats(s.data);
            setTeam(t.data);
            if (m.data.is_partner && m.data.partner_status !== "active" && m.data.partner_status !== "expired" && m.data.partner_status !== "suspended") {
                nav("/partner/activate", { replace: true });
            }
        } catch {
            nav("/login?next=/partner/dashboard", { replace: true });
        }
    };
    useEffect(() => { load(); }, []); // eslint-disable-line

    // Load a curated property feed once the profile is known (top 12 latest active).
    useEffect(() => {
        if (!me?.is_partner) return;
        api.get("/properties", { params: { limit: 12, status: "active" } })
            .then((r) => setProperties(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
            .catch(() => setProperties([]));
    }, [me?.is_partner]);

    const filteredProperties = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return properties;
        return properties.filter((p) =>
            [p.title, p.city, p.locality, p.property_type, p.subtype].filter(Boolean).join(" ").toLowerCase().includes(s)
        );
    }, [q, properties]);

    if (!me) return (
        <div className="min-h-screen bg-navy-950 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
        </div>
    );

    if (!me.is_partner) {
        return (
            <div className="text-on-image min-h-screen bg-navy-950 flex items-center justify-center px-6">
                <div className="max-w-md text-center rounded-3xl glass p-8">
                    <Handshake className="w-12 h-12 text-gold-500 mx-auto" />
                    <h1 className="font-heading text-2xl font-bold text-white mt-4">Not a GPM Partner yet?</h1>
                    <p className="text-white/70 mt-2 text-sm">Register a GPM Partner account to unlock this dashboard.</p>
                    <Link to="/refer" className="mt-6 inline-flex h-11 px-6 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 font-bold items-center gap-2">Learn more <ExternalLink className="w-4 h-4" /></Link>
                </div>
            </div>
        );
    }

    const c = stats?.commission || {};
    const l = stats?.leads || {};
    const isActive = me.partner_status === "active";
    const shareProperty = (p) => {
        const link = `${window.location.origin}/properties/${p.id}?ref=${me.referral_code || ""}`;
        return { link, waMsg: `🏠 *${p.title}* — ${formatINR(p.price)}\n📍 ${[p.locality, p.city].filter(Boolean).join(", ")}\n\nDetails 👉 ${link}\n\n_Shared via GPM Partner ${me.partner_id || ""}_` };
    };

    return (
        <div className="text-on-image min-h-screen bg-navy-950 grid-pattern px-4 sm:px-6 py-8" data-testid="partner-dashboard">
            <Seo title="GPM Partner Dashboard" description="Your Partner profile, referral link, commission and team." path="/partner/dashboard" />
            <div className="max-w-6xl mx-auto space-y-6">

                {/* 1) PARTNER PROFILE */}
                <div className="rounded-3xl border border-gold-500/40 bg-gradient-to-br from-navy-900 via-navy-950 to-navy-900 p-6 sm:p-7 shadow-[0_0_40px_rgba(218,165,32,0.15)]" data-testid="partner-profile-card">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-14 h-14 rounded-2xl bg-gold-500/15 border border-gold-500/40 flex items-center justify-center shrink-0"><Handshake className="w-7 h-7 text-gold-500" /></div>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500 font-extrabold">🤝 GPM Partner Profile</div>
                                <div className="font-heading text-xl sm:text-2xl font-bold text-white leading-tight mt-0.5" data-testid="partner-id">{me.partner_id || "—"}</div>
                                <div className="text-xs text-white/60 mt-1 truncate">Name: <span className="text-white font-semibold">{me.package?.name}</span></div>
                            </div>
                        </div>
                        <div className="text-right text-xs">
                            <div className="mb-1">Status: <span className={`font-bold uppercase text-[11px] tracking-wider ${isActive ? "text-emerald-400" : me.partner_status === "expired" ? "text-amber-400" : "text-rose-400"}`} data-testid="partner-status">{me.partner_status}</span></div>
                            {me.partner_expires_at && <div className="text-white/70">Valid till <b className="text-white">{new Date(me.partner_expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</b></div>}
                        </div>
                    </div>
                </div>

                {/* 2) ALL GPM PROPERTIES */}
                <section data-testid="section-all-properties">
                    <SectionHeader icon="🏠" title="All GPM Properties" subtitle="Browse the full eligible catalogue and share with attribution to your Partner ID." />
                    <div className="rounded-2xl bg-navy-900 border border-white/10 p-4 mb-3 flex items-center gap-2">
                        <Search className="w-4 h-4 text-white/50 ml-1" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, city or type…" className="flex-1 bg-transparent text-white placeholder:text-white/40 outline-none text-sm" data-testid="partner-property-search" />
                        <Link to="/properties" className="text-xs font-bold text-gold-500 hover:text-gold-400 shrink-0" data-testid="browse-all-properties">Browse all →</Link>
                    </div>
                    {filteredProperties.length === 0 ? (
                        <EmptyCard msg={q ? `No properties match "${q}"` : "Loading catalogue…"} />
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredProperties.map((p) => (
                                <PropertyShareCard key={p.id} p={p} share={shareProperty} />
                            ))}
                        </div>
                    )}
                </section>

                {/* 3) REFER & EARN */}
                <section data-testid="section-refer-earn">
                    <SectionHeader icon="🎁" title="Refer & Earn" subtitle="Every property share carries your attribution. Track leads and eligible commission below." />

                    <div className="rounded-3xl bg-gradient-to-br from-navy-900 via-navy-950 to-navy-900 border border-gold-500/30 p-6">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-gold-500 font-extrabold mb-3">Your Referral Link</div>
                        <div className="flex flex-col sm:flex-row items-stretch gap-2">
                            <input readOnly value={me.referral_link || ""} className="flex-1 h-11 px-4 rounded-xl bg-navy-950 border border-gold-500/20 text-white text-sm font-mono" data-testid="partner-referral-link" />
                            <button onClick={() => { navigator.clipboard.writeText(me.referral_link); toast.success("Link copied!"); }} className="h-11 px-4 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-bold flex items-center justify-center gap-2" data-testid="partner-copy-link"><Copy className="w-4 h-4" /> Copy</button>
                            <a href={`https://wa.me/?text=${encodeURIComponent(`Discover verified properties on Global Property Mart: ${me.referral_link}`)}`} target="_blank" rel="noreferrer" className="h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-navy-950 text-sm font-bold flex items-center justify-center gap-2" data-testid="partner-whatsapp-share">WhatsApp</a>
                            <button onClick={async () => { if (navigator.share) { try { await navigator.share({ title: "GPM", url: me.referral_link }); } catch { /* cancel */ } } else { navigator.clipboard.writeText(me.referral_link); toast.success("Copied"); } }} className="h-11 px-4 rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 text-sm font-bold flex items-center justify-center gap-2" data-testid="partner-share-btn"><Share2 className="w-4 h-4" /> Share</button>
                        </div>
                        <div className="text-xs text-white/55 mt-2">Referral Code: <b className="text-gold-500 font-mono">{me.referral_code}</b></div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                        <Kpi icon={Users} label="Total Leads" value={l.total || 0} testid="kpi-total-leads" />
                        <Kpi icon={TrendingUp} label="This Month" value={l.this_month || 0} testid="kpi-month-leads" />
                        <Kpi icon={IndianRupee} label="Approved" value={`₹${(c.approved || 0).toLocaleString("en-IN")}`} testid="kpi-approved" />
                        <Kpi icon={Wallet} label="Wallet" value={`₹${(c.wallet_balance || 0).toLocaleString("en-IN")}`} testid="kpi-wallet" />
                    </div>

                    <div className="rounded-3xl bg-navy-900 border border-white/10 p-6 mt-4">
                        <div className="flex items-center gap-2 mb-4">
                            <Sparkles className="w-5 h-5 text-gold-500" />
                            <div className="font-heading text-base font-bold text-white">Eligible commission breakdown</div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Stat label="Pending verification" value={`₹${(c.eligible_pending || 0).toLocaleString("en-IN")}`} tone="amber" testid="stat-pending" />
                            <Stat label="Approved (awaiting payout)" value={`₹${(c.approved || 0).toLocaleString("en-IN")}`} tone="emerald" testid="stat-approved" />
                            <Stat label="Paid to wallet" value={`₹${(c.paid || 0).toLocaleString("en-IN")}`} tone="gold" testid="stat-paid" />
                        </div>
                        <p className="text-[11px] text-white/50 mt-4 leading-relaxed">Commission is calculated on the backend as per the applicable GPM policy and eligibility rules. Displayed amounts are indicative and subject to verification, refund/reversal windows and GPM Terms &amp; Conditions.</p>
                    </div>
                </section>

                {/* 4) MY TEAM */}
                <section data-testid="section-my-team">
                    <SectionHeader icon="👥" title="My Team" subtitle="Users who signed up using your referral code. Team growth is informational — commission is only paid on eligible property/service transactions." />

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <Kpi icon={Users} label="Team size" value={team?.total || 0} testid="team-total" />
                        <Kpi icon={UserCheck} label="Active" value={team?.active || 0} testid="team-active" />
                        <Kpi icon={UserX} label="Pending verify" value={team?.pending || 0} testid="team-pending" />
                        <Kpi icon={Handshake} label="Direct Partners" value={team?.gpm_partners || 0} testid="team-direct-partners" />
                    </div>

                    {(!team || team.items?.length === 0) ? (
                        <EmptyCard msg="No team members yet — share your referral link to start growing your GPM team." />
                    ) : (
                        <div className="rounded-2xl bg-navy-900 border border-white/10 overflow-hidden">
                            <div className="hidden sm:grid grid-cols-[1fr_140px_120px_120px] px-4 py-2 bg-navy-950 border-b border-white/10 text-[10px] uppercase tracking-widest text-white/50 font-bold">
                                <span>Member</span><span>Role</span><span>Status</span><span className="text-right">Joined</span>
                            </div>
                            <div className="divide-y divide-white/5">
                                {team.items.map((m) => (
                                    <div key={m.id} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_120px_120px] gap-2 sm:gap-0 px-4 py-3 items-center" data-testid={`team-row-${m.id}`}>
                                        <div>
                                            <div className="font-semibold text-white text-sm">{m.name}</div>
                                            <div className="text-[11px] text-white/50 truncate">{m.email}</div>
                                        </div>
                                        <div className="text-xs text-white/70 capitalize">{(m.role || "user").replace(/_/g, " ")}</div>
                                        <div>{m.email_verified ? <span className="text-[10px] font-bold text-emerald-400">✓ ACTIVE</span> : <span className="text-[10px] font-bold text-amber-400">PENDING</span>}</div>
                                        <div className="sm:text-right text-[11px] text-white/60">{m.created_at ? new Date(m.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

/* ---------------- small building blocks ---------------- */
function SectionHeader({ icon, title, subtitle }) {
    return (
        <div className="mb-3">
            <div className="flex items-center gap-2">
                <span className="text-lg">{icon}</span>
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">{title}</h2>
            </div>
            {subtitle && <p className="text-xs sm:text-sm text-white/60 mt-1 max-w-3xl">{subtitle}</p>}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-2xl bg-navy-900 border border-white/10 hover:border-gold-500/40 p-4 transition-colors" data-testid={testid}>
            <Icon className="w-5 h-5 text-gold-500" />
            <div className="text-[11px] uppercase tracking-widest text-white/50 mt-2 font-bold">{label}</div>
            <div className="font-heading text-xl font-bold text-white mt-0.5">{value}</div>
        </div>
    );
}

function Stat({ label, value, tone, testid }) {
    const toneMap = { amber: "border-amber-500/40 bg-amber-500/5", emerald: "border-emerald-500/40 bg-emerald-500/5", gold: "border-gold-500/40 bg-gold-500/5" };
    return (
        <div className={`rounded-xl border ${toneMap[tone]} p-4`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{label}</div>
            <div className="font-heading text-2xl font-bold text-white mt-2">{value}</div>
        </div>
    );
}

function EmptyCard({ msg }) {
    return <div className="rounded-2xl bg-navy-900 border border-dashed border-white/10 p-8 text-center text-sm text-white/55">{msg}</div>;
}

function PropertyShareCard({ p, share }) {
    const { link, waMsg } = share(p);
    const img = p.images?.[0] ? (p.images[0].startsWith("http") ? p.images[0] : fileUrl(p.images[0])) : "";
    return (
        <div className="rounded-2xl bg-navy-900 border border-white/10 hover:border-gold-500/40 overflow-hidden group transition-colors" data-testid={`share-card-${p.id}`}>
            <Link to={`/properties/${p.id}`} className="block">
                <div className="aspect-[16/10] bg-navy-800 overflow-hidden">
                    {img ? <img src={img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center text-white/30 text-xs">No image</div>}
                </div>
                <div className="p-4">
                    <div className="font-heading font-bold text-white text-sm line-clamp-1">{p.title}</div>
                    <div className="text-[11px] text-white/55 mt-0.5 truncate">{[p.locality, p.city].filter(Boolean).join(", ")}</div>
                    <div className="text-gold-500 font-bold mt-2">{formatINR(p.price)}</div>
                </div>
            </Link>
            <div className="px-3 pb-3 flex gap-1.5">
                <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copied"); }} className="flex-1 h-8 rounded-md bg-white/10 hover:bg-white/15 text-white text-[11px] font-bold flex items-center justify-center gap-1" data-testid={`copy-${p.id}`}><Copy className="w-3 h-3" /> Copy</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noreferrer" className="flex-1 h-8 rounded-md bg-emerald-500 hover:bg-emerald-400 text-navy-950 text-[11px] font-bold flex items-center justify-center gap-1" data-testid={`wa-${p.id}`}><MessageCircle className="w-3 h-3" /> WA</a>
                <button onClick={async () => { if (navigator.share) { try { await navigator.share({ title: p.title, url: link }); } catch { /* cancel */ } } else { navigator.clipboard.writeText(link); toast.success("Copied"); } }} className="flex-1 h-8 rounded-md bg-gold-500 hover:bg-gold-400 text-navy-950 text-[11px] font-bold flex items-center justify-center gap-1" data-testid={`share-${p.id}`}><Share2 className="w-3 h-3" /> Share</button>
            </div>
        </div>
    );
}
